/**
 * The design assistant, as Cloud supplies it to the designer.
 *
 * The designer's `ai` option is the SDK's bring-your-own-model hook, and Cloud is
 * one more host of it: nothing here talks to a model. It posts the designer's
 * request to the Cloud API, which calls the model, checks the answer, asks again
 * when it fails and meters every attempt against the workspace's allowance.
 *
 * Three decisions are worth their reasons.
 *
 * **Repairs happen on the server, so `maxRepairs` is 0.** Every attempt costs
 * money, and the allowance is counted where the money is spent. A second loop in
 * the tab would repeat requests the service had already repaired and billed.
 *
 * **The answer goes back to the designer as a tool input, not as a template.**
 * The service only ever saw the design with its images replaced by `asset:N`, so
 * it returns those placeholders; the designer's own check is what puts the logo
 * back, and it checks the whole thing again while it does.
 *
 * **The allowance is words and a bar, never money.** A dollar figure would be an
 * internal cost dressed as a price; "approx. 7 generations left" is the thing a
 * person can decide with.
 */
import { TemplateAiError, type TemplateAiProvider } from "@broadpaper/core/ai";
import { ApiError, api, type AiAllowanceView } from "../api.js";

/** Where a Free workspace goes to upgrade: the plan screen with both prices shown. */
const UPGRADE = { label: "Upgrade to Pro", href: "/account?plan=pro" };

export function cloudAiProvider(opts: { onAllowance?: (allowance: AiAllowanceView) => void } = {}): TemplateAiProvider {
  return {
    maxRepairs: 0,

    async generate(request, signal) {
      try {
        const answer = await api.aiTemplate(request, signal);
        return answer.status === "refused" ? { outcome: "refused", reason: answer.reason } : { outcome: "template", template: answer.template, ...(answer.notes ? { notes: answer.notes } : {}) };
      } catch (e) {
        throw toAiError(e, signal);
      }
    },

    async allowance() {
      try {
        const view = await api.aiAllowance();
        opts.onAllowance?.(view);
        if (!view.enabled) return null;
        return { used: view.used, label: allowanceLabel(view.approxLeft) };
      } catch {
        // A bar that failed to load is not worth an error on top of the designer.
        return null;
      }
    },

    decided(requestId, decision) {
      // Fire and forget: it records what happened and changes nothing either way.
      void api.aiDecision(requestId, decision).catch(() => undefined);
    }
  };
}

export function allowanceLabel(approxLeft: number): string {
  if (approxLeft <= 0) return "This month's allowance is used";
  return `approx. ${approxLeft} generation${approxLeft === 1 ? "" : "s"} left`;
}

/**
 * The service's failure, in the designer's words.
 *
 * The service's own message is already written for a person — it never carries
 * anything from the model provider — so it is kept; only the kind is translated.
 */
export function toAiError(e: unknown, signal?: AbortSignal): TemplateAiError {
  if (signal?.aborted || (e as { name?: string })?.name === "AbortError") return new TemplateAiError("cancelled", "Cancelled.");
  if (!(e instanceof ApiError)) return new TemplateAiError("failed", "Something went wrong while designing that. Please try again.");
  const hint = e.hint ? { hint: e.hint } : {};
  switch (e.code) {
    case "limit-reached":
      return new TemplateAiError("allowance", e.message, { ...hint, ...(e.detail?.upgradeTo ? { action: UPGRADE } : {}) });
    case "rate-limited":
      return new TemplateAiError("rate-limited", e.message, { ...hint, ...(typeof e.detail?.retryAfter === "number" ? { retryAfter: e.detail.retryAfter } : {}) });
    case "unavailable":
      return new TemplateAiError("unavailable", e.message, hint);
    case "offline":
      // A request that never got an answer is not always the person's connection.
      // A gateway that gives up on a long request, or a service mid-restart,
      // answers without our CORS headers, and the browser reports that exactly as
      // it reports being offline. If the browser believes it is online, say so.
      return typeof navigator !== "undefined" && navigator.onLine === false
        ? new TemplateAiError("unavailable", e.message, hint)
        : new TemplateAiError("unavailable", "The design assistant didn't answer.", { hint: "Try again in a minute. Nothing you have done has been lost." });
    case "unprocessable":
      return new TemplateAiError("invalid", e.message, hint);
    default:
      return new TemplateAiError("failed", e.message, hint);
  }
}
